import { PopupContainerContext } from '@distribusion/owl-frontend-ui'
import { APIProvider } from '@vis.gl/react-google-maps'
import React, { ReactElement, Suspense, lazy } from 'react'
import { RouterProvider, useRouteError } from 'react-router-dom'

import ErrorPage from '@components/ErrorPage'
import { NotificationModalProvider } from '@components/NotificationModal/Provider'
import ProtectedRoute from '@components/ProtectedRoute'
import config from '@config'
import { URLAppContextProvider } from '@context/app'
import { UserAuthProvider } from '@hooks/userAuthProvider/provider'
import amplitude from '@lib/analytics/amplitude'
import { QueryClientProvider } from '@lib/queryClient'
import Sentry from '@lib/sentry'
import CheckoutPage from '@pages/Checkout/App'
import RestrictedRouter from '@pages/Index/RestrictedRouter'
import NotFoundPage from '@pages/NotFound'
import SearchPage from '@pages/Search/App'
import SearchResultPage from '@pages/SearchResult/App'

const ReservationAgentPayment = lazy(() => import('@pages/ReservationAgentPayment/App'))
const ReservationFailedPayment = lazy(() => import('@pages/ReservationFailedPayment/App'))
const ReservationPayment = lazy(() => import('@pages/ReservationPayment/App'))
const ConfirmationPage = lazy(() => import('@pages/Confirmation/App'))
const DestinationsPage = lazy(() => import('@pages/Destinations/App'))
const FramePage = lazy(() => import('@pages/Frame/App'))
const FrameReservationPayment = lazy(() => import('@pages/Frame/ReservationPayment'))
const FrameSearchResults = lazy(() => import('@pages/Frame/SearchResult'))
const TrackTrip = lazy(() => import('@pages/TrackTrip/App'))
const TrackTripSearch = lazy(() => import('@pages/TrackTrip/Search'))
const ServicePage = lazy(() => import('@pages/Service/App'))
const StatusPage = lazy(() => import('@pages/Status/App'))
const TemplatePage = lazy(() => import('@pages/Template/App'))
const AirportLanding = lazy(() => import('@pages/Landing/App'))
const LoginPage = lazy(() => import('@pages/Booking/Login'))
const PaymentPage = lazy(() => import('@pages/Booking/Payment'))
const ShowPage = lazy(() => import('@pages/Booking/ShowPage'))
const CityLanding = lazy(() => import('@pages/Landing/City'))
const SubscriptionPage = lazy(() => import('@pages/Subscription/App'))
const SubscriptionConfirmationPage = lazy(() => import('@pages/Subscription/Confirmation/App'))
const AccountPage = lazy(() => import('@pages/Account/App'))
const ResetPasswordPage = lazy(() => import('@pages/Account/ResetPassword'))
const AccountDeletedPage = lazy(() => import('@pages/Account/AccountDeleted'))
const AccountActivationPage = lazy(() => import('@pages/Subscription/AccountActivation'))
const ActionRequiredPage = lazy(() => import('@pages/ActionRequired/App'))
const SubscriptionDetailsPage = lazy(() => import('@pages/SubscriptionDetails/App'))
const SubscriptionBookingDetailsPage = lazy(() => import('@pages/SubscriptionBookingDetails/App'))
const CancellationPage = lazy(() => import('@pages/Cancellation/App'))
const ProductsPage = lazy(() => import('@pages/Products/App'))

const RouteErrorBoundary = (): ReactElement => {
  const error = useRouteError()

  return <ErrorPage error={error as Error} code="internalError" />
}

const Root = () => {
  return (
    <URLAppContextProvider>
      <RestrictedRouter />
    </URLAppContextProvider>
  )
}

const router = Sentry.createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <SearchPage /> },
      { path: '/checkout', element: <CheckoutPage /> },
      {
        path: '/frame',
        element: <FramePage />,
        children: [
          { path: 'result', element: <FrameSearchResults /> },
          { path: 'reservation/pay/:id', element: <FrameReservationPayment /> },
        ],
      },
      { path: '/result', element: <SearchResultPage /> },
      {
        path: '/landing',
        children: [
          { index: true, element: <AirportLanding /> },
          { path: 'checkout', element: <CheckoutPage /> },
          {
            path: 'city',
            children: [
              { index: true, element: <CityLanding /> },
              { path: 'checkout', element: <CheckoutPage /> },
            ],
          },
        ],
      },
      { path: '/subscription', element: <SubscriptionPage /> },
      { path: '/subscription/:subscriptionId', element: <SubscriptionDetailsPage /> },
      { path: '/subscriptions/confirmation/:subscriptionId', element: <SubscriptionConfirmationPage /> },
      { path: '/subscription-booking/:id', element: <SubscriptionBookingDetailsPage /> },
      { path: '/subscription/cancellation', element: <CancellationPage /> },
      { path: '/account/activation/:userId', element: <AccountActivationPage /> },
      { path: '/account/account-deleted', element: <AccountDeletedPage /> },
      {
        path: '/booking',
        children: [
          { path: 'login', element: <LoginPage /> },
          { path: ':id', element: <ShowPage /> },
          { path: ':id/payment', element: <PaymentPage /> },
        ],
      },
      {
        path: '/account/change-password/:userId',
        element: <ResetPasswordPage />,
      },
      {
        path: '/account/*',
        element: (
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        ),
      },
      { path: '/bookings/confirmation/:bookingId', element: <ConfirmationPage /> },
      { path: '/reservation/pay/:id', element: <ReservationPayment /> },
      { path: '/reservation/actionRequired/:reservationId', element: <ActionRequiredPage /> },
      { path: '/agent/:id/reservation/pay', element: <ReservationAgentPayment /> },
      { path: '/reservation/pay/:id/failure', element: <ReservationFailedPayment /> },
      { path: '/status/:bookingFormId', element: <StatusPage /> },
      { path: '/destinations', element: <DestinationsPage /> },
      { path: '/products', element: <ProductsPage /> },
      { path: '/service', element: <ServicePage /> },
      { path: '/d/*', element: <TemplatePage /> },
      { path: '/trackTrip/:id', element: <TrackTrip /> },
      { path: '/trackTrip', element: <TrackTripSearch /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

router.subscribe(() => {
  amplitude.onInternalRedirect()
})

Sentry.init({ global: true })

const App = (): ReactElement => (
  <Suspense>
    <QueryClientProvider>
      <UserAuthProvider>
        <APIProvider apiKey={config.googleMapsPublicKey}>
          <NotificationModalProvider>
            <PopupContainerContext.Provider value={{ current: document.body }}>
              <RouterProvider router={router} />
            </PopupContainerContext.Provider>
          </NotificationModalProvider>
        </APIProvider>
      </UserAuthProvider>
    </QueryClientProvider>
  </Suspense>
)

export default App
